import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UI_THEME } from '../ui/theme';
import { useAuth } from '../context/AuthContext';
import { Permission } from '../types/user.types';
import PainelTratamentoSolicitacao from '../components/PainelTratamentoSolicitacao';
import StatusPedido from '../components/StatusPedido';
import {
  STATUS_SOLICITACAO_EXPLICACAO,
  STATUS_SOLICITACAO_LABEL,
  rotuloAlvo,
  rotuloCategoria,
  rotuloPedido,
  solicitacaoService,
  ultimaMensagemDaEquipe,
} from '../services/solicitacaoService';

const formatarDataHora = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR');
};

const formatarTamanho = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const ACAO_LABEL: Record<string, string> = {
  ABERTA: 'Solicitação aberta',
  ANEXO_ADICIONADO: 'Anexo adicionado',
  ASSUMIDA: 'Assumida pela equipe',
  RESPOSTA: 'Resposta da equipe',
  ATENDIDA: 'Solicitação atendida',
  RECUSADA: 'Solicitação recusada',
};

/** Para quem fez o pedido, o andamento fala em linguagem simples. */
const ACAO_LABEL_SOLICITANTE: Record<string, string> = {
  ABERTA: 'Você enviou o pedido',
  ANEXO_ADICIONADO: 'Arquivo juntado',
  ASSUMIDA: 'A equipe começou a analisar',
  RESPOSTA: 'A equipe respondeu',
  ATENDIDA: 'Pedido resolvido',
  RECUSADA: 'Pedido não aceito',
};

const SolicitacaoDetalhePage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const podeTratar = hasPermission(Permission.TRATAR_SOLICITACAO_ALTERACAO);
  const nomeSecao = podeTratar ? 'Suporte e solicitações' : 'Fale com a equipe';
  const isSaas = UI_THEME === 'saas';
  const accent = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const surface = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['solicitacoes', 'detalhe', id],
    queryFn: () => solicitacaoService.obter(id),
    enabled: Boolean(id),
    retry: false,
  });

  if (isLoading) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Carregando solicitação...</p>;
  }
  if (isError || !data) {
    return (
      <div className={`rounded-3xl border p-6 ${surface}`}>
        <p className="text-sm text-slate-700 dark:text-slate-200">Solicitação não encontrada.</p>
        <Link to="/dashboard/solicitacoes" className={`mt-3 inline-block text-sm font-semibold hover:underline ${accent}`}>
          Voltar para {nomeSecao}
        </Link>
      </div>
    );
  }

  if (!podeTratar) {
    const resposta = ultimaMensagemDaEquipe(data);
    return (
      <div className="w-full space-y-5">
        <Link to="/dashboard/solicitacoes" className={`inline-flex min-h-[44px] items-center text-base font-semibold hover:underline ${accent}`}>
          ← {nomeSecao}
        </Link>
        <section className={`rounded-3xl border p-5 sm:p-6 ${surface}`}>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{rotuloPedido(data)}</h1>
          <p className="mt-1 text-base text-slate-600 dark:text-slate-300">
            {rotuloCategoria(data.categoria, data.categoriaOutro)} · {rotuloAlvo(data)}
          </p>
          <div className="mt-4">
            <StatusPedido status={data.status} grande />
            <p className="mt-2 text-base leading-7 text-slate-700 dark:text-slate-200">
              {STATUS_SOLICITACAO_EXPLICACAO[data.status] ?? ''}
            </p>
          </div>
          {resposta && (
            <div className="mt-5 rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-700/60 dark:bg-emerald-500/10">
              <p className="text-base font-bold text-emerald-900 dark:text-emerald-200">Resposta da equipe</p>
              <p className="mt-1 whitespace-pre-wrap text-lg leading-8 text-slate-900 dark:text-slate-100">{resposta}</p>
            </div>
          )}
          <div className="mt-5">
            <p className="text-base font-semibold text-slate-800 dark:text-slate-100">O que você pediu</p>
            <p className="mt-1 whitespace-pre-wrap text-base leading-7 text-slate-700 dark:text-slate-200">{data.descricao}</p>
          </div>
          {(data.emendaId || data.preProjetoId) && (
            <Link
              to={data.emendaId ? `/dashboard/emendas/${data.emendaId}` : `/dashboard/pre-projetos/${data.preProjetoId}`}
              className={`mt-4 inline-flex min-h-[44px] items-center text-base font-semibold hover:underline ${accent}`}
            >
              Ver {rotuloAlvo(data)}
            </Link>
          )}
        </section>

        <section className={`rounded-3xl border p-5 sm:p-6 ${surface}`}>
          <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Andamento</h2>
          <ol className="mt-3 space-y-4 border-l-2 border-slate-200 pl-4 dark:border-slate-700">
            {data.historico.map((h, i) => (
              <li key={`${h.criadaEm}-${i}`}>
                <p className="text-base font-semibold text-slate-900 dark:text-slate-100">{ACAO_LABEL_SOLICITANTE[h.acao] ?? h.acao}</p>
                {h.observacao && h.acao !== 'ABERTA' && <p className="text-base text-slate-600 dark:text-slate-300">{h.observacao}</p>}
                <p className="text-sm text-slate-500 dark:text-slate-400">{formatarDataHora(h.criadaEm)}</p>
              </li>
            ))}
          </ol>
        </section>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-3xl border p-6 ${surface}`}>
        <Link to="/dashboard/solicitacoes" className={`text-xs font-semibold hover:underline ${accent}`}>
          ← {nomeSecao}
        </Link>
        <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{rotuloPedido(data)} <span className="text-base font-normal text-slate-500">({data.protocolo})</span></h1>
        <dl className="mt-4 grid grid-cols-1 gap-4 text-sm sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Status</dt>
            <dd className="mt-1 font-semibold text-slate-900 dark:text-slate-100">
              {STATUS_SOLICITACAO_LABEL[data.status] ?? data.status}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Refere-se a</dt>
            <dd className="mt-1">
              {data.emendaId ? (
                <Link to={`/dashboard/emendas/${data.emendaId}`} className={`font-semibold hover:underline ${accent}`}>
                  {rotuloAlvo(data)}
                </Link>
              ) : data.preProjetoId ? (
                <Link to={`/dashboard/pre-projetos/${data.preProjetoId}`} className={`font-semibold hover:underline ${accent}`}>
                  {rotuloAlvo(data)}
                </Link>
              ) : (
                <span className="text-slate-900 dark:text-slate-100">{rotuloAlvo(data)}</span>
              )}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Categoria</dt>
            <dd className="mt-1 text-slate-900 dark:text-slate-100">{rotuloCategoria(data.categoria, data.categoriaOutro)}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Solicitante</dt>
            <dd className="mt-1 text-slate-900 dark:text-slate-100">{data.solicitanteNome}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Responsável</dt>
            <dd className="mt-1 text-slate-900 dark:text-slate-100">{data.responsavelNome ?? 'Ninguém assumiu ainda'}</dd>
          </div>
        </dl>
        {data.rota && (
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            Aberta na tela{data.tituloTela ? ` "${data.tituloTela}"` : ''}:{' '}
            {data.rota.startsWith('/dashboard') ? (
              <Link to={data.rota} className={`break-all font-mono hover:underline ${accent}`}>{data.rota}</Link>
            ) : (
              <span className="break-all font-mono">{data.rota}</span>
            )}
          </p>
        )}
        <p className="mt-5 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">{data.descricao}</p>
      </section>

      {podeTratar && <PainelTratamentoSolicitacao solicitacao={data} />}

      <section className={`rounded-3xl border p-5 ${surface}`}>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Anexos</h2>
        {data.anexos.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Nenhum anexo.</p>
        ) : (
          <ul className="mt-3 space-y-1 text-sm text-slate-700 dark:text-slate-200">
            {data.anexos.map((a) => (
              <li key={a.id}>{a.nomeOriginal} <span className="text-xs text-slate-400">({formatarTamanho(a.tamanhoBytes)})</span></li>
            ))}
          </ul>
        )}
      </section>

      <section className={`rounded-3xl border p-5 ${surface}`}>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Andamento</h2>
        <ol className="mt-3 space-y-3 border-l border-slate-200 pl-4 dark:border-slate-700">
          {data.historico.map((h, i) => (
            <li key={`${h.criadaEm}-${i}`} className="text-sm">
              <p className="font-semibold text-slate-900 dark:text-slate-100">{ACAO_LABEL[h.acao] ?? h.acao}</p>
              {h.observacao && <p className="text-slate-600 dark:text-slate-300">{h.observacao}</p>}
              <p className="text-xs text-slate-400">{h.usuario} · {formatarDataHora(h.criadaEm)}</p>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
};

export default SolicitacaoDetalhePage;
